package ai.wagemix.web.screens.mixbuilder

import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import ai.wagemix.web.components.WMMenuItem
import ai.wagemix.web.components.WMMenuButton
import ai.wagemix.web.components.WMBadge
import ai.wagemix.web.components.WMBadgeKind
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMButtonSize
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.screens.WMWebStrings
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import org.jetbrains.compose.web.css.px

/**
 * The builder's primary actions (Penpot top bar: [Re-evaluate] [Update mix] [Share], or [Save mix]
 * for a new draft); stateless. Re-evaluate shows only for a mix generated from a job description.
 * Save and Update are disabled unless the draft is [savable] (an enabled occupation has data). While the mix is
 * [reevaluating] every action is disabled and Re-evaluate says it is running.
 *
 * Below Medium the top bar has no room for them all (Ryan, 2026-11-01): it keeps the main action (Save mix or Update
 * mix), Share and Re-evaluate move into a "More actions" menu beside it, and the AI mix badge is left to the page's
 * AI notice. While the mix is re-evaluated the menu is left out, since its items cannot be disabled.
 */
@Composable
fun WMMixBuilderActions(
    strings: WMWebStrings,
    savable: Boolean,
    sourceMixId: String?,
    dirty: Boolean,
    fromJobDescription: Boolean,
    reevaluating: Boolean,
    modifier: Modifier = Modifier,
    onSaveClick: () -> Unit,
    onUpdateClick: () -> Unit,
    onShareClick: () -> Unit,
    onReevaluateClick: () -> Unit
) {
    val mb = strings.mixBuilder
    val aiMix = sourceMixId != null && fromJobDescription
    Row(modifier = Modifier.gap(8.px).then(modifier), verticalAlignment = Alignment.CenterVertically) {
        Row(modifier = Modifier.gap(8.px).displayIfAtLeast(Breakpoint.MD), verticalAlignment = Alignment.CenterVertically) {
            if (aiMix) {
                // The saved mix was generated from a job description: the same teal AI badge its saved-mix card shows.
                WMBadge(mb.aiMix, kind = WMBadgeKind.AI, icon = WMIconGlyph.Sparkles)
                WMButton(
                    if (reevaluating) mb.reevaluating else strings.jobDescriptions.reevaluate,
                    kind = WMButtonKind.Secondary,
                    size = WMButtonSize.Sm,
                    enabled = !reevaluating,
                    leadingIcon = WMIconGlyph.RefreshCw,
                    onClick = onReevaluateClick,
                )
            }
            WMMainAction(strings, savable, sourceMixId, dirty, reevaluating, onSaveClick, onUpdateClick)
            if (sourceMixId != null) {
                WMButton(
                    mb.share,
                    kind = WMButtonKind.Secondary,
                    size = WMButtonSize.Sm,
                    enabled = !reevaluating,
                    leadingIcon = WMIconGlyph.Share2,
                    onClick = onShareClick
                )
            }
        }
        Row(modifier = Modifier.gap(4.px).displayUntil(Breakpoint.MD), verticalAlignment = Alignment.CenterVertically) {
            WMMainAction(strings, savable, sourceMixId, dirty, reevaluating, onSaveClick, onUpdateClick)
            val more = listOfNotNull(
                WMMenuItem(mb.share, WMIconGlyph.Share2, onSelect = onShareClick).takeIf { sourceMixId != null },
                WMMenuItem(strings.jobDescriptions.reevaluate, WMIconGlyph.RefreshCw, onSelect = onReevaluateClick).takeIf { aiMix },
            )
            if (more.isNotEmpty() && !reevaluating) {
                WMMenuButton(accessibilityString = mb.moreActions, items = more)
            }
        }
    }
}

/** Update mix for a saved mix, Save mix for a new draft. */
@Composable
private fun WMMainAction(
    strings: WMWebStrings,
    savable: Boolean,
    sourceMixId: String?,
    dirty: Boolean,
    reevaluating: Boolean,
    onSaveClick: () -> Unit,
    onUpdateClick: () -> Unit
) {
    val mb = strings.mixBuilder
    if (sourceMixId != null) {
        WMButton(
            mb.updateMix,
            size = WMButtonSize.Sm,
            enabled = dirty && savable && !reevaluating,
            leadingIcon = WMIconGlyph.Check,
            onClick = onUpdateClick
        )
    } else {
        WMButton(mb.saveMix, size = WMButtonSize.Sm, enabled = savable, leadingIcon = WMIconGlyph.Bookmark, onClick = onSaveClick)
    }
}
